import { useId, type ReactNode } from 'react';
import { Dialog } from '../../primitives/Dialog/Dialog';
import { ModalHeader } from '../ModalHeader/ModalHeader';
import styles from './Drawer.module.css';

/**
 * Figma: `Drawer` (2024:31283) — a panel docked to the right edge over an
 * `Overlay`: `_Drawer container` holding the same `_Modal header` as `Modal`,
 * then a `Slot` that takes the rest of the height.
 *
 * No variant properties; the only component property is the `Slot`, which is
 * `children`. There is no actions bar — the design gives a drawer none, so a
 * drawer that needs one puts it in the slot.
 *
 * Built on the native `<dialog>` — see `Dialog` for the behaviour added beyond
 * Figma.
 */
export type DrawerProps = {
  open: boolean;
  /** Escape, the overlay and the close button all call this. */
  onClose: () => void;
  title: ReactNode;
  /** Figma `Supporting text`. */
  description?: ReactNode;
  /** Not in Figma. Three lines and `See more` — see `ModalHeader`. */
  clampDescription?: boolean;
  /** Figma `Featured icon`. */
  icon?: ReactNode;
  /** Figma `X close button`. On by default, as drawn. */
  showClose?: boolean;
  closeLabel?: string;
  /** Figma `Slot`. */
  children?: ReactNode;
};

export function Drawer({
  open,
  onClose,
  title,
  description,
  clampDescription,
  icon,
  showClose = true,
  closeLabel,
  children,
}: DrawerProps) {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={description !== undefined ? descriptionId : undefined}
      className={styles.layer}
    >
      <div className={styles.panel}>
        <ModalHeader
          title={title}
          description={description}
          clampDescription={clampDescription}
          icon={icon}
          onClose={showClose ? onClose : undefined}
          closeLabel={closeLabel}
          titleId={titleId}
          descriptionId={descriptionId}
        />
        <div className={styles.body}>{children}</div>
      </div>
    </Dialog>
  );
}
